import { create } from 'zustand';

export type LiveKitStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface LiveKitState {
  status: LiveKitStatus;
  roomName: string | null;
  micEnabled: boolean;
  camEnabled: boolean;
  speakerEnabled: boolean;
  /** Participant identity strings – changes trigger VideoGrid re-renders */
  participantIds: string[];
  /** Increments when tracks change within existing participants */
  trackVersion: number;
  error: string | null;
  errorUrl: string | null;
  errorStack: string | null;
  /** true while a proximity call (not a formal meeting) is shown in VideoGrid */
  isProxCall: boolean;
  // Actions
  setStatus: (status: LiveKitStatus) => void;
  setRoomName: (name: string | null) => void;
  setMicEnabled: (v: boolean) => void;
  setCamEnabled: (v: boolean) => void;
  setSpeakerEnabled: (v: boolean) => void;
  setParticipantIds: (ids: string[]) => void;
  bumpTrackVersion: () => void;
  setError: (error: string | null, url?: string | null, stack?: string | null) => void;
  setIsProxCall: (v: boolean) => void;
  reset: () => void;
}

export const useLiveKitStore = create<LiveKitState>((set) => ({
  status: 'idle',
  roomName: null,
  micEnabled: false,
  camEnabled: false,
  speakerEnabled: true,
  participantIds: [],
  trackVersion: 0,
  error: null,
  errorUrl: null,
  errorStack: null,
  isProxCall: false,

  setStatus: (status) => set({ status }),
  setRoomName: (roomName) => set({ roomName }),
  setMicEnabled: (micEnabled) => set({ micEnabled }),
  setCamEnabled: (camEnabled) => set({ camEnabled }),
  setSpeakerEnabled: (speakerEnabled) => set({ speakerEnabled }),
  setParticipantIds: (ids) => set((s) => {
    const prev = s.participantIds;
    if (ids.length === prev.length && ids.every((id, i) => id === prev[i])) return s;
    return { participantIds: ids };
  }),
  bumpTrackVersion: () => set((s) => ({ trackVersion: s.trackVersion + 1 })),
  setError: (error, url = null, stack = null) => set({ error, errorUrl: url, errorStack: stack }),
  setIsProxCall: (isProxCall) => set({ isProxCall }),
  reset: () => set({
    status: 'idle',
    roomName: null,
    micEnabled: false,
    camEnabled: false,
    participantIds: [],
    trackVersion: 0,
    error: null,
    errorUrl: null,
    errorStack: null,
    isProxCall: false,
  }),
}));
